import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, MenuItem, TextField, Paper, Typography, Stack, Grid, Chip, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import KeyIcon from '@mui/icons-material/Key';

const STRATEGY_OPTIONS = [
  { value: 'BULL_PUT_CREDIT_SPREAD', label: 'Bull Put Credit Spread' },
  { value: 'BEAR_CALL_CREDIT_SPREAD', label: 'Bear Call Credit Spread' },
  { value: 'IRON_CONDOR', label: 'Iron Condor' },
  { value: 'EM_BULL_PUT', label: 'EM Bull Put (research — paper only)' },
  { value: 'SHORT_STRANGLE', label: 'Short Strangle (naked baseline — never traded live)' },
];

const QualificationPage: React.FC = () => {
  const [strategy, setStrategy] = useState<string>('BULL_PUT_CREDIT_SPREAD');
  const [readinessData, setReadinessData] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [noDataHint, setNoDataHint] = useState<string | null>(null);

  useEffect(() => {
    setReadinessData(null);
    setLoadError(null);
    setNoDataHint(null);
    optionsService
      .getReadiness(strategy, '1.0.0')
      .then((data) => setReadinessData(data))
      .catch((err) => {
        const body = err?.response?.data;
        if (body?.code === 'NO_PUBLISHED_REPORT') {
          setNoDataHint(body.hint || body.error);
          return;
        }
        setLoadError(body?.error || err?.message || 'Failed to load the qualification report');
      });
  }, [strategy]);

  const published = readinessData?.published;

  const cert = readinessData?.certificate;
  const isApproved = readinessData?.overallDeploymentVerdict === 'APPROVED_FOR_LIVE';

  type Status = 'PASS' | 'FAIL' | 'NOT_EVALUATED';
  const statusOf = (g: any): Status => (g?.status as Status) ?? (g?.passed ? 'PASS' : 'FAIL');
  const r = readinessData;
  const gates: Array<{ name: string; detail: string; status: Status; evidence?: string }> = r
    ? [
        {
          name: 'Gate 1: Engineering Correctness',
          detail:
            r.engineeringGate?.tsClean === null || r.engineeringGate?.tsClean === undefined
              ? 'Type check and tests not run for this report'
              : `TypeScript clean: ${r.engineeringGate.tsClean}; tests passed: ${r.engineeringGate.testsPassed}`,
          status: statusOf(r.engineeringGate),
          evidence: r.engineeringGate?.evidence,
        },
        {
          name: 'Gate 2: Historical Performance',
          detail: `${r.historicalGate?.totalTrades ?? '—'} trades, Profit Factor ${r.historicalGate?.profitFactor?.toFixed?.(2) ?? '—'}, Win Rate ${r.historicalGate?.winRate?.toFixed?.(2) ?? '—'}%, Max DD ${r.historicalGate?.maxDrawdownPct?.toFixed?.(2) ?? '—'}%`,
          status: statusOf(r.historicalGate),
          evidence: r.historicalGate?.evidence,
        },
        {
          name: 'Gate 3: Data Quality Audit',
          detail: `${r.dataQualityGate?.qualityScorePct?.toFixed?.(2) ?? '—'}% clean bars in the recorded dataset`,
          status: statusOf(r.dataQualityGate),
          evidence: r.dataQualityGate?.evidence,
        },
        {
          name: 'Gate 4: Monte Carlo Tail Risk (10k Runs)',
          detail: `Worst simulated DD ${r.monteCarloGate?.worstDrawdownPct?.toFixed?.(2) ?? '—'}%, Ruin probability ${r.monteCarloGate?.ruinProbPct?.toFixed?.(2) ?? '—'}%${
            r.monteCarloGate?.lossProbPct !== undefined ? `, P(net loss) ${r.monteCarloGate.lossProbPct.toFixed(1)}% (max 20%)` : ''
          }`,
          status: statusOf(r.monteCarloGate),
          evidence: r.monteCarloGate?.evidence,
        },
        {
          name: 'Gate 5: Catastrophic Black Swan Stress',
          detail: `${r.catastrophicStressGate?.scenariosPassed ?? '—'} / 8 shocks survived`,
          status: statusOf(r.catastrophicStressGate),
          evidence: r.catastrophicStressGate?.evidence,
        },
        {
          name: 'Gate 6: Strategy Mutation Diff',
          detail: r.mutationGate?.details || '—',
          status: statusOf(r.mutationGate),
          evidence: r.mutationGate?.evidence,
        },
        {
          name: 'Gate 7: Paper Realism Score',
          detail:
            (r.paperRealismGate?.sampleCount ?? 0) === 0 && r.paperRealismGate?.status
              ? 'No paper-vs-live execution samples yet'
              : `${r.paperRealismGate?.realismScorePct?.toFixed?.(1) ?? '—'}% execution realism vs backtest`,
          status: statusOf(r.paperRealismGate),
          evidence: r.paperRealismGate?.evidence,
        },
        {
          name: 'Gate 8: Shadow Mode Stability',
          detail: `${r.shadowStabilityGate?.interceptedEvents ?? 0} shadow signals intercepted`,
          status: statusOf(r.shadowStabilityGate),
          evidence: r.shadowStabilityGate?.evidence,
        },
      ]
    : [];
  const counts = {
    passed: gates.filter((g) => g.status === 'PASS').length,
    failed: gates.filter((g) => g.status === 'FAIL').length,
    notEvaluated: gates.filter((g) => g.status === 'NOT_EVALUATED').length,
  };
  const STATUS_STYLE: Record<Status, { label: string; color: string; bg: string }> = {
    PASS: { label: 'PASS', color: '#16A34A', bg: 'rgba(34, 197, 94, 0.12)' },
    FAIL: { label: 'FAIL', color: '#DC2626', bg: 'rgba(220, 38, 38, 0.12)' },
    NOT_EVALUATED: { label: 'NOT EVALUATED', color: '#64748B', bg: '#F1F5F9' },
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* 11-Gate Checklist */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <VerifiedIcon sx={{ color: '#16A34A' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                STRATEGY QUALIFICATION MATRIX
              </Typography>
            </Stack>
            <TextField
              select
              size="small"
              label="Strategy"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              sx={{ minWidth: 320, mb: 1.5, mt: 0.5 }}
            >
              {STRATEGY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            {published && (
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2 }}>
                Published report · computed {new Date(published.generatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
                {published.generatedBy ? ` by ${published.generatedBy}` : ''}
                {published.gitCommit ? ` · commit ${published.gitCommit}` : ''}
                <br />
                Data: {published.dataCoverage || '—'}
                {published.dateRange ? ` (${published.dateRange.from} → ${published.dateRange.to})` : ''}
              </Typography>
            )}
            {published?.freshness === 'STALE' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>STALE — strategy code changed since this report</AlertTitle>
                The server is running different strategy logic than the version that was qualified. Re-run and
                publish the report.
                {published.changedFiles?.length > 0 && (
                  <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', fontSize: 12 }}>
                    Changed: {published.changedFiles.slice(0, 8).join(', ')}
                    {published.changedFiles.length > 8 ? ` +${published.changedFiles.length - 8} more` : ''}
                  </Box>
                )}
              </Alert>
            )}
            {published?.freshness === 'CURRENT' && (
              <Chip size="small" label={`Current · strategy fingerprint ${String(published.strategyFingerprint).slice(0, 12)}`} sx={{ mb: 2, bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 700 }} />
            )}
            {published?.freshness === 'UNKNOWN' && (
              <Chip size="small" label="Freshness unknown (report or server build has no strategy fingerprint)" sx={{ mb: 2, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
            )}

            {loadError && (
              <Typography variant="body2" sx={{ color: '#DC2626', mb: 2 }}>
                Failed to load readiness evaluation: {loadError}
              </Typography>
            )}
            {noDataHint && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>No qualification report published yet</AlertTitle>
                Qualification runs offline on recorded market data (never simulated prices, and never on the trading
                server) and the result is published here.
                <Box component="span" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', fontSize: 12 }}>{noDataHint}</Box>
              </Alert>
            )}
            {!loadError && !noDataHint && !readinessData && (
              <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                Loading the latest published qualification report…
              </Typography>
            )}

            <List disablePadding>
              {gates.map((g, i) => (
                <ListItem key={i} disableGutters sx={{ py: 0.8, borderBottom: '1px solid #F1F5F9' }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {g.status === 'PASS' ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                    ) : g.status === 'FAIL' ? (
                      <CancelIcon sx={{ fontSize: 18, color: '#DC2626' }} />
                    ) : (
                      <RemoveCircleOutlineIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={g.name}
                    secondary={g.status === 'NOT_EVALUATED' && g.evidence ? g.evidence : g.detail}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.82rem', color: '#0F172A' }}
                    secondaryTypographyProps={{ fontSize: '0.72rem', color: '#64748B' }}
                  />
                  <Chip
                    size="small"
                    label={STATUS_STYLE[g.status].label}
                    sx={{
                      height: 20,
                      bgcolor: STATUS_STYLE[g.status].bg,
                      color: STATUS_STYLE[g.status].color,
                      fontWeight: 700,
                      fontSize: '0.65rem',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Cryptographic Certificate & Decision */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2.5}>
            {/* Qualification Decision */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: '#0F172A', color: '#FFFFFF', textAlign: 'center', border: '1px solid #1E293B' }}>
              <Typography variant="overline" sx={{ color: '#38BDF8', fontWeight: 700, letterSpacing: 1.5 }}>
                FINAL PRODUCTION VERDICT
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: isApproved ? '#4ADE80' : counts.failed > 0 ? '#F87171' : '#CBD5E1', my: 1 }}>
                {!readinessData
                  ? '⏳ NO REPORT'
                  : isApproved
                  ? '🟢 QUALIFIED FOR LIVE PRODUCTION'
                  : counts.failed > 0
                  ? '🔴 BLOCKED BY GOVERNANCE'
                  : '⚪ BLOCKED: INSUFFICIENT EVIDENCE'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                {readinessData
                  ? isApproved
                    ? `All ${gates.length} qualification gates passed on evidence.`
                    : `${counts.failed} failed · ${counts.passed} passed · ${counts.notEvaluated} not evaluated. A certificate needs every gate to pass on evidence.`
                  : 'No published qualification report for this strategy.'}
              </Typography>
            </Paper>

            {/* Cryptographic Certificate */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <KeyIcon sx={{ color: '#0284C7' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B' }}>
                  CRYPTOGRAPHIC QUALIFICATION CERTIFICATE
                </Typography>
              </Stack>

              <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #E2E8F0', fontFamily: "'Roboto Mono', monospace", fontSize: '0.72rem' }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      CERTIFICATE ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284C7' }}>
                      {cert?.certificateId || 'Not issued — strategy not yet approved'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      GIT COMMIT SIGNATURE
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cert?.gitCommit || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      PARAMETER HASH (SHA-256)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cert?.parameterHash ? `sha256:${cert.parameterHash}` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      DATASET CHECKSUM
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cert?.datasetChecksum || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      HMAC-SHA256 SIGNATURE
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#16A34A', fontWeight: 600, wordBreak: 'break-all' }}>
                      {cert?.signature ? `${cert.signature.substring(0, 28)}... (VERIFIED)` : '—'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QualificationPage;
